<template>
  <div class="inventory-preview-list inventory-preview-list--glass">
    <div class="inventory-preview-details-section inventory-preview-details-section--glass">
      <div class="inventory-preview-section-header inventory-preview-section-header--glass">
        <div class="inventory-preview-section-heading">
          <i class="bi bi-box-seam inventory-preview-section-icon" aria-hidden="true"></i>
          <h3 class="inventory-preview-section-title">Item overview</h3>
        </div>
        <button
          v-if="selectedItem"
          type="button"
          class="inventory-preview-label"
          @click="toggleDetailsCard"
        >
          {{ showDetailsCard ? 'Hide details' : 'Show details' }}
        </button>
      </div>

      <div class="inventory-preview-header inventory-preview-header--glass">
        <div v-if="!selectedItem" class="inventory-preview-no-selection">
          <i class="bi bi-cursor inventory-preview-empty-icon" aria-hidden="true"></i>
          <span>Select an inventory item to view stock and activity</span>
        </div>

        <div v-else class="inventory-preview-details-card inventory-preview-details-card--glass">
          <div class="inventory-preview-card-header inventory-preview-card-header--glass">
            <div class="inventory-preview-card-identity">
              <div class="inventory-preview-avatar" aria-hidden="true">
                <i class="bi bi-box-seam"></i>
              </div>
              <div class="inventory-preview-card-title-wrap">
                <div class="inventory-preview-card-title">{{ selectedItem.name }}</div>
                <div class="inventory-preview-card-meta">
                  <span
                    v-if="selectedItem.role"
                    class="inventory-role-badge"
                    :class="`inventory-role-badge--${selectedItem.role}`"
                  ><i :class="selectedItem.roleIcon" aria-hidden="true"></i> {{ selectedItem.roleLabel }}</span>
                  <span class="inventory-preview-meta-chip">{{ selectedItem.group || 'No category' }}</span>
                  <span class="inventory-preview-meta-chip inventory-preview-meta-chip--muted">
                    {{ selectedItem.no }}
                  </span>
                </div>
              </div>
            </div>

            <div class="inventory-preview-card-balance" title="On hand quantity">
              <span class="inventory-preview-balance-label">On hand:</span>
              <span class="inventory-preview-balance-amount">{{ selectedItem.qty }}</span>
            </div>

            <div class="inventory-preview-card-actions">
              <SecondaryButton
                label="Edit"
                icon="bi bi-pencil"
                compact
                custom-class="inventory-preview-action-btn"
                @click="goToEditItem"
              />
              <SecondaryButton
                label="Adjust stock"
                icon="bi bi-sliders"
                compact
                custom-class="inventory-preview-action-btn"
                @click="goToStockAdjustment"
              />
            </div>
          </div>

          <div
            class="inventory-preview-finance-summary inventory-preview-finance-summary--glass"
            :class="{ 'inventory-preview-finance-summary--loading': loadingTransactions }"
          >
            <div class="inventory-preview-finance-stat-grid">
              <div
                v-for="card in quantityStatCards"
                :key="card.key"
                class="inventory-preview-finance-stat-card"
                :class="`inventory-preview-finance-stat-card--${card.tone}`"
              >
                <div
                  class="inventory-preview-finance-stat-card-icon"
                  :class="`inventory-preview-finance-stat-card-icon--${card.tone}`"
                >
                  <i :class="card.icon" aria-hidden="true"></i>
                </div>
                <div class="inventory-preview-finance-stat-card-content">
                  <span class="inventory-preview-finance-stat-card-title">{{ card.title }}</span>
                  <div class="inventory-preview-finance-stat-card-metrics inventory-preview-finance-stat-card-metrics--primary-cols">
                    <div
                      v-for="(metric, idx) in card.metrics"
                      :key="`${card.key}-${idx}`"
                      class="inventory-preview-finance-stat-card-metric"
                    >
                      <span class="inventory-preview-finance-stat-card-col-label">{{ metric.label }}</span>
                      <span
                        class="inventory-preview-finance-stat-card-col-value"
                        :class="metric.valueClass"
                      >{{ metric.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showDetailsCard && selectedItem" class="inventory-preview-expandable-details inventory-preview-expandable-details--glass">
        <div class="inventory-preview-details-content">
          <div class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-boxes"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Available qty</div>
              <div class="inventory-preview-detail-value">{{ selectedItem.qty }}</div>
            </div>
          </div>
          <div class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-flag"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Initial qty</div>
              <div class="inventory-preview-detail-value">{{ formatInitialQty(selectedItem) }}</div>
            </div>
          </div>
          <div v-if="selectedItem.role === 'finished'" class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-tools"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Built qty</div>
              <div class="inventory-preview-detail-value">{{ movementTotals.built }}</div>
            </div>
          </div>
          <div v-if="selectedItem.role === 'part'" class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-diagram-3"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Used in builds</div>
              <div class="inventory-preview-detail-value">{{ movementTotals.usedInBuilds }}</div>
            </div>
          </div>
          <div v-if="selectedItem.role !== 'part'" class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-receipt"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Sold qty</div>
              <div class="inventory-preview-detail-value">{{ movementTotals.sold }}</div>
            </div>
          </div>
          <div class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-calendar3"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">As of date</div>
              <div class="inventory-preview-detail-value">{{ selectedItem.asOfDate || '—' }}</div>
            </div>
          </div>
          <div v-if="selectedItem.standardCost != null" class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-cash-stack"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Standard cost</div>
              <div class="inventory-preview-detail-value">{{ formatPrice(selectedItem.standardCost) }}</div>
            </div>
          </div>
          <div v-if="selectedItem.salesPrice != null" class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-tag"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Sales price</div>
              <div class="inventory-preview-detail-value">{{ formatPrice(selectedItem.salesPrice) }}</div>
            </div>
          </div>
          <div v-if="selectedItem.purchasePrice != null" class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-cart"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Purchase price</div>
              <div class="inventory-preview-detail-value">{{ formatPrice(selectedItem.purchasePrice) }}</div>
            </div>
          </div>
          <div v-if="selectedItem.costFromParts != null" class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-calculator"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Cost (from parts)</div>
              <div class="inventory-preview-detail-value">{{ formatPrice(selectedItem.costFromParts) }}</div>
            </div>
          </div>
          <div class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-collection"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Category</div>
              <div class="inventory-preview-detail-value">{{ selectedItem.group }}</div>
            </div>
          </div>
          <div class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-building"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Warehouse</div>
              <div class="inventory-preview-detail-value">{{ selectedItem.warehouse }}</div>
            </div>
          </div>
          <div class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-hash"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Item no</div>
              <div class="inventory-preview-detail-value">{{ selectedItem.no }}</div>
            </div>
          </div>
          <div v-if="selectedItem.usedIn && selectedItem.usedIn.length" class="inventory-preview-detail-item">
            <span class="inventory-preview-detail-icon" aria-hidden="true"><i class="bi bi-diagram-3"></i></span>
            <div class="inventory-preview-detail-content">
              <div class="inventory-preview-detail-label">Used in</div>
              <div class="inventory-preview-detail-value">{{ selectedItem.usedIn.join(', ') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="inventory-preview-transactions-section inventory-preview-transactions-section--glass">
      <div class="inventory-preview-section-header inventory-preview-section-header--glass inventory-preview-section-header--txn">
        <div class="inventory-preview-section-heading">
          <i class="bi bi-clock-history inventory-preview-section-icon" aria-hidden="true"></i>
          <h3 class="inventory-preview-section-title">Quantity history</h3>
        </div>
        <span v-if="selectedItem" class="inventory-preview-txn-count">
          {{ filteredTransactions.length }} record{{ filteredTransactions.length === 1 ? '' : 's' }}
        </span>
      </div>

      <div class="inventory-preview-transactions-filters">
        <div class="inventory-preview-filter-field">
          <LabelDropdown
            label="Current View"
            select-id="inventory-txn-view"
            v-model="localCurrentViewFilter"
            inline
            :searchable="false"
            :options="viewFilterOptions"
          />
        </div>
        <div class="inventory-preview-filter-field">
          <LabelDropdown
            label="Date"
            select-id="inventory-txn-date"
            v-model="localDateFilter"
            inline
            :searchable="false"
            :options="dateFilterOptions"
          />
        </div>
      </div>

      <div class="inventory-preview-table-responsive consultant-glass-table-responsive consultant-glass-table-responsive--framed">
        <table class="inventory-preview-transactions-table consultant-glass-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>No</th>
              <th>Memo</th>
              <th>Qty change</th>
              <th>Balance</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loadingTransactions">
              <td colspan="7" class="inventory-preview-loading">Loading quantity history...</td>
            </tr>
            <tr v-else-if="!selectedItem">
              <td colspan="7" class="inventory-preview-no-transactions">Select an item to view quantity history</td>
            </tr>
            <tr v-else-if="paginatedTransactions.length === 0">
              <td colspan="7" class="inventory-preview-no-transactions">No quantity history found for this item</td>
            </tr>
            <template v-else>
              <tr
                v-for="(transaction, idx) in paginatedTransactions"
                :key="transaction.id"
                class="inventory-preview-transaction-row consultant-glass-table-row"
                :style="{ '--ipl-row-delay': `${Math.min(idx * 0.03, 0.45)}s` }"
              >
                <td>{{ transaction.date }}</td>
                <td>
                  <span
                    :class="[
                      'inventory-preview-txn-type',
                      transaction.transactionType,
                      transaction.type === 'Sale' ? 'is-sale' : '',
                      transaction.type === 'Adjustment' ? 'is-adjustment' : '',
                      transaction.type === 'Purchase' ? 'is-purchase' : '',
                      transaction.type === 'Opening' ? 'is-opening' : '',
                    ]"
                  >{{ transaction.type }}</span>
                </td>
                <td class="inventory-preview-txn-no">{{ transaction.no }}</td>
                <td class="inventory-preview-txn-memo" :title="transaction.memo">{{ transaction.memo }}</td>
                <td
                  :class="{
                    'inventory-preview-qty-pos': transaction.qty > 0,
                    'inventory-preview-qty-neg': transaction.qty < 0,
                  }"
                >{{ formatQtyChange(transaction) }}</td>
                <td class="inventory-preview-balance-cell">
                  {{ transaction.balanceAfter != null ? transaction.balanceAfter : '—' }}
                </td>
                <td
                  :class="{
                    'inventory-preview-amount-pos': transaction.amount > 0,
                    'inventory-preview-amount-neg': transaction.amount < 0,
                  }"
                >${{ Number(transaction.amount || 0).toFixed(2) }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="inventory-preview-transactions-pagination">
        <PaginationComponent
          :current-page="currentPage"
          :total-items="filteredTransactions.length"
          :items-per-page="itemsPerPage"
          item-label="history records"
          :show-items-per-page="true"
          @page-change="handlePageChange"
          @items-per-page-change="handleItemsPerPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PaginationComponent from '@/components/PaginationComponent.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import logger from '@/utils/logger';

const VIEW_FILTER_OPTIONS = Object.freeze([
  { value: 'all', label: 'All History' },
  { value: 'opening', label: 'Opening' },
  { value: 'quotes', label: 'Quotes' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'cashSales', label: 'Cash Sales' },
  { value: 'customerCreditNotes', label: 'Customer Credit Notes' },
  { value: 'cashPurchases', label: 'Cash Purchases' },
  { value: 'vendorCreditNotes', label: 'Vendor Credit Notes' },
  { value: 'bills', label: 'Bills' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'stockAdjustments', label: 'Stock Adjustments' },
  { value: 'builds', label: 'Assembly Builds' },
]);

const DATE_FILTER_OPTIONS = Object.freeze([
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisFinancialYear', label: 'This Financial Year' },
  { value: 'lastFinancialYear', label: 'Last Financial Year' },
  { value: 'thisFinancialQuarter', label: 'This Financial Quarter' },
  { value: 'lastFinancialQuarter', label: 'Last Financial Quarter' },
  { value: 'financialYearToDate', label: 'Financial Year To Date' },
]);

export default {
  name: 'InventoryPreviewList',
  components: {
    PaginationComponent,
    LabelDropdown,
    SecondaryButton,
  },
  emits: ['page-change', 'items-per-page-change', 'view-filter-changed', 'date-filter-changed'],
  props: {
    selectedItem: {
      type: Object,
      default: null
    },
    transactions: {
      type: Array,
      default: () => []
    },
    loadingTransactions: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    currentViewFilter: {
      type: String,
      default: 'all'
    },
    dateFilter: {
      type: String,
      default: 'all'
    }
  },
  data() {
    return {
      showDetailsCard: false,
      localCurrentViewFilter: this.currentViewFilter,
      localDateFilter: this.dateFilter
    };
  },
  computed: {
    viewFilterOptions() {
      return VIEW_FILTER_OPTIONS;
    },
    dateFilterOptions() {
      return DATE_FILTER_OPTIONS;
    },
    movementTotals() {
      const totals = { built: 0, usedInBuilds: 0, sold: 0 };
      for (const txn of this.transactions || []) {
        const qty = Number(txn?.qty) || 0;
        if (txn?.transactionType === 'builds') {
          if (qty > 0) totals.built += qty;
          else totals.usedInBuilds += Math.abs(qty);
        } else if (txn?.transactionType === 'invoices') {
          totals.sold += Math.abs(qty);
        }
      }
      return totals;
    },
    quantityStatCards() {
      if (!this.selectedItem) return [];
      const item = this.selectedItem;
      const { built, usedInBuilds, sold } = this.movementTotals;
      return [
        {
          key: 'stock',
          title: 'Stock quantities',
          tone: 'stock',
          icon: 'bi bi-boxes',
          metrics: [
            { label: 'On hand', value: String(item.qty ?? 0) },
            { label: 'Initial', value: String(this.formatInitialQty(item)) },
            item.role === 'finished' && { label: 'Built', value: String(built) },
            item.role === 'part' && { label: 'Used in builds', value: String(usedInBuilds) },
            item.role !== 'part' && { label: 'Sold', value: String(sold) },
            { label: 'As of', value: item.asOfDate || '—' },
          ].filter(Boolean),
        },
        {
          key: 'pricing',
          title: 'Pricing & location',
          tone: 'pricing',
          icon: 'bi bi-currency-dollar',
          metrics: [
            item.salesPrice != null && { label: 'Sales price', value: this.formatPrice(item.salesPrice) },
            item.purchasePrice != null && { label: 'Purchase price', value: this.formatPrice(item.purchasePrice) },
            item.costFromParts != null && { label: 'Cost (from parts)', value: this.formatPrice(item.costFromParts) },
            item.standardCost != null && { label: 'Std cost', value: this.formatPrice(item.standardCost) },
            { label: 'Warehouse', value: item.warehouse || '—' },
          ].filter(Boolean),
        },
      ];
    },
    filteredTransactions() {
      if (!this.transactions || !Array.isArray(this.transactions)) {
        return [];
      }
      
      let txns = [...this.transactions];
      
      // Filter by transaction view
      if (this.localCurrentViewFilter && this.localCurrentViewFilter !== 'all') {
        txns = txns.filter(t => t && t.transactionType === this.localCurrentViewFilter);
      }
      
      // Filter by date
      if (this.localDateFilter && this.localDateFilter !== 'all') {
        try {
          txns = this.filterByDate(txns, this.localDateFilter);
        } catch (error) {
          logger.warn('Error filtering by date:', error);
        }
      }
      
      // Sort by date descending with null safety
      txns.sort((a, b) => {
        if (!a || !b) return 0;
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      });
      
      return txns;
    },
    paginatedTransactions() {
      if (!this.filteredTransactions || !Array.isArray(this.filteredTransactions)) {
        return [];
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredTransactions.slice(start, end);
    }
  },
  methods: {
    formatInitialQty(item) {
      if (!item) return '—';
      if (item.initialQuantity != null && item.initialQuantity !== '') {
        return Number(item.initialQuantity) || 0;
      }
      return item.qty != null ? Number(item.qty) || 0 : '—';
    },
    formatPrice(value) {
      return `$${Number(value).toFixed(2)}`;
    },
    formatQtyChange(transaction) {
      if (!transaction) return '—';
      const qty = Number(transaction.qty) || 0;
      if (transaction.type === 'Opening' || transaction.transactionType === 'opening') {
        return qty;
      }
      if (qty > 0) return `+${qty}`;
      return String(qty);
    },
    goToEditItem() {
      if (!this.selectedItem?.id) return;
      const companyId = this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/inventory/edit-item/${this.selectedItem.id}`);
    },
    goToStockAdjustment() {
      const companyId = this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/inventory/stock-adjustment/new`);
    },
    toggleDetailsCard() {
      this.showDetailsCard = !this.showDetailsCard;
    },
    handlePageChange(page) {
      this.$emit('page-change', page);
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.$emit('items-per-page-change', itemsPerPage);
    },
    filterByDate(transactions, dateFilter) {
      if (!dateFilter || dateFilter === 'all' || !transactions || !Array.isArray(transactions)) {
        return transactions || [];
      }
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      return transactions.filter(txn => {
        if (!txn || !txn.date) return false;
        try {
          const txnDate = new Date(txn.date);
          
          switch(dateFilter) {
            case 'today':
              return txnDate.toDateString() === today.toDateString();
            case 'thisWeek': {
              const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
              return txnDate >= weekStart;
            }
            case 'thisMonth':
              return txnDate.getMonth() === today.getMonth() && 
                     txnDate.getFullYear() === today.getFullYear();
            case 'lastWeek': {
              const lastWeekStart = new Date(today.setDate(today.getDate() - today.getDay() - 7));
              const lastWeekEnd = new Date(today.setDate(today.getDate() + 6));
              return txnDate >= lastWeekStart && txnDate <= lastWeekEnd;
            }
            case 'lastMonth': {
              const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
              return txnDate >= lastMonth && txnDate <= lastMonthEnd;
            }
            default:
              return true;
          }
        } catch (error) {
          logger.warn('Error parsing transaction date:', error);
          return false;
        }
      });
    }
  },
  watch: {
    currentViewFilter(newVal) {
      this.localCurrentViewFilter = newVal;
    },
    dateFilter(newVal) {
      this.localDateFilter = newVal;
    },
    localCurrentViewFilter() {
      this.$emit('view-filter-changed', this.localCurrentViewFilter);
    },
    localDateFilter() {
      this.$emit('date-filter-changed', this.localDateFilter);
    }
  }
};
</script>

<style scoped>
@import '../styles/InventoryPreviewList.css';
@import '../styles/inventory-preview-list-glass.css';
</style>

